/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { ReactNode } from 'react';
import { Check, CircleAlert, Minus, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Icon } from './Icon';
import { Meter } from './Kpi';
import { percent } from './format';

/** Page layout: panels stacked 24 px apart (the page's own column). */
export function Stack({ children }: { children: ReactNode }) {
  return <div className="ui-stack">{children}</div>;
}

/** Two or four equal columns that fold to one (two for KPIs) at phone width. */
export function Grid({ cols = 2, children, label }: { cols?: 2 | 3 | 4; children: ReactNode; label?: string }) {
  return <div className={`ui-grid ui-grid--${cols}`} role={label ? 'group' : undefined} aria-label={label}>{children}</div>;
}

/** A short code-like label: an app key, a tool or model name. */
export function Tag({ children, mono = true, title }: { children: ReactNode; mono?: boolean; title?: string }) {
  return <span className={`ui-tag${mono ? ' is-mono' : ''}`} title={title}>{children}</span>;
}

/** A share of a whole: a thin bar and its percentage. */
export function ShareBar({ value, label, digits = 1 }: { value: number; label: string; digits?: number }) {
  return (
    <span className="ui-share">
      <Meter value={value} label={label} size="sm" />
      <span className="ui-share-text">{percent(value, digits)}</span>
    </span>
  );
}

export interface MeterRowProps {
  label: ReactNode;
  /** The figure on the right, such as "$46.12 of $150.00". */
  figure: ReactNode;
  /** From 0 to 1; no bar when null (nothing to measure against). */
  value: number | null;
  meterLabel: string;
  tone?: 'data' | 'accent' | 'ok' | 'warn' | 'bad';
  footStart?: ReactNode;
  footEnd?: ReactNode;
}

/** A labelled fill bar with a line under it: spend against a cap, a quota. */
export function MeterRow({ label, figure, value, meterLabel, tone = 'data', footStart, footEnd }: MeterRowProps) {
  return (
    <div className="ui-meter-row">
      <div className="ui-meter-row-top">
        <span className="ui-meter-row-label">{label}</span>
        <span className="ui-meter-row-figure">{figure}</span>
      </div>
      {value === null ? null : <Meter value={value} label={meterLabel} tone={tone} />}
      {footStart || footEnd ? (
        <div className="ui-meter-row-foot">
          <span>{footStart}</span>
          <span>{footEnd}</span>
        </div>
      ) : null}
    </div>
  );
}

export type HealthTone = 'ok' | 'warn' | 'bad' | 'neutral';

const HEALTH_ICONS: Record<HealthTone, LucideIcon> = { ok: Check, warn: TriangleAlert, bad: CircleAlert, neutral: Minus };

/** A list of things the service reports about itself, one row each. */
export function HealthList({ label, children }: { label: string; children: ReactNode }) {
  return <ul className="ui-health" aria-label={label}>{children}</ul>;
}

export interface HealthRowProps {
  tone: HealthTone;
  title: ReactNode;
  sub?: ReactNode;
  /** A status chip or a meter on the right. */
  side?: ReactNode;
}

export function HealthRow({ tone, title, sub, side }: HealthRowProps) {
  return (
    <li className="ui-health-row">
      <span className={`ui-health-icon is-${tone}`}><Icon icon={HEALTH_ICONS[tone]} size="xs" /></span>
      <span className="ui-health-text">
        <span className="ui-health-title">{title}</span>
        {sub ? <span className="ui-health-sub">{sub}</span> : null}
      </span>
      {side ? <span className="ui-health-side">{side}</span> : null}
    </li>
  );
}
